import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Envelope, TestTube, Eye, ArrowCounterClockwise, CaretRight,
} from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Admin UI to manage every user-facing transactional email template.
 * Renders an accordion of templates fetched from `/api/admin/email-templates`.
 * Each row exposes Subject, Body (WYSIWYG), CTA button text, plus Preview / Test / Reset.
 */
export default function EmailTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data);
      if (!openKey && data.length) setOpenKey(data[0].key);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to load templates");
    } finally { setLoading(false); }
  }, [openKey]);

  useEffect(() => { load(); }, [load]);

  if (loading && templates.length === 0) {
    return <div className="sticker p-6 mt-6 tech-label text-[var(--gc-text-muted)]">LOADING TEMPLATES…</div>;
  }

  return (
    <div className="sticker p-6 md:p-8 mt-6" data-testid="email-templates-section">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 bg-[var(--gc-orange)] text-white flex items-center justify-center border-2 border-[var(--gc-navy)]">
          <Envelope size={22} weight="bold" />
        </div>
        <h2 className="font-display text-3xl">Email Templates</h2>
      </div>
      <p className="text-sm text-[var(--gc-text-muted)] mb-6">
        Every user-facing transactional email is editable here — subject, body and CTA button text. Blank fields fall back to the built-in default. The GeekCreative logo header and the footer are always added automatically.
      </p>

      <div className="border border-[var(--gc-border)]">
        {templates.map((t) => (
          <TemplateRow
            key={t.key}
            template={t}
            open={openKey === t.key}
            onToggle={() => setOpenKey(openKey === t.key ? null : t.key)}
            onSaved={load}
          />
        ))}
      </div>
    </div>
  );
}

function TemplateRow({ template, open, onToggle, onSaved }) {
  const savedSubject = template.saved?.subject || "";
  const savedBody = template.saved?.body_html || "";
  const savedCta = template.saved?.cta_text || "";

  const [subject, setSubject] = useState(savedSubject);
  const [body, setBody] = useState(savedBody);
  const [ctaText, setCtaText] = useState(savedCta);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Keep local state in sync when parent reloads templates (e.g. after Save / Reset)
  useEffect(() => { setSubject(savedSubject); setBody(savedBody); setCtaText(savedCta); }, [savedSubject, savedBody, savedCta]);

  const hasOverride = !!(savedSubject || savedBody || savedCta);
  const dirty = subject !== savedSubject || body !== savedBody || ctaText !== savedCta;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/email-templates/${template.key}`, {
        subject, body_html: body, cta_text: ctaText,
      });
      toast.success(`${template.label} — saved`);
      onSaved?.();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm(`Reset "${template.label}" back to the built-in default? Your edits will be discarded.`)) return;
    try {
      await api.delete(`/admin/email-templates/${template.key}`);
      toast.info("Reverted to default.");
      onSaved?.();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Reset failed"); }
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${template.key}/preview`, {
        subject, body_html: body, cta_text: ctaText,
      });
      setPreview(data);
      setPreviewOpen(true);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const sendTest = async () => {
    if (dirty && !window.confirm("You have unsaved changes — send the LAST SAVED version? Cancel to save first.")) return;
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${template.key}/test`);
      toast.success(`Test sent to ${data.sent_to}`);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Test send failed"); }
    finally { setTesting(false); }
  };

  const insertToken = (tk) => {
    setBody((b) => (/<\/p>\s*$/.test(b) ? b.replace(/<\/p>(\s*)$/, `${tk}</p>$1`) : (b || template.defaults.body_html) + tk));
  };

  return (
    <div className={`border-b border-[var(--gc-border)] last:border-b-0 ${open ? "bg-white" : ""}`} data-testid={`email-tpl-row-${template.key}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-[var(--gc-bg-alt)] transition-colors"
        data-testid={`email-tpl-toggle-${template.key}`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <CaretRight
            size={16}
            weight="bold"
            className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          />
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-2">
              {template.label}
              {hasOverride && (
                <span className="text-[10px] font-mono-tech tracking-widest uppercase text-[var(--gc-orange)] border border-[var(--gc-orange)] px-1.5 py-0.5" data-testid={`email-tpl-custom-${template.key}`}>
                  Custom
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--gc-text-muted)] truncate">{template.description}</div>
          </div>
        </div>
        <div className="font-mono-tech text-xs text-[var(--gc-text-muted)] uppercase tracking-widest shrink-0 ml-4">{template.key}</div>
      </button>

      {open && (
        <div className="px-5 pb-6 space-y-5">
          {/* Subject */}
          <div>
            <div className="tech-label mb-2">Subject</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={template.defaults.subject}
              className="gc-input"
              data-testid={`email-tpl-subject-${template.key}`}
            />
          </div>

          {/* Tokens + Body */}
          <div>
            <div className="tech-label mb-2 flex items-center justify-between">
              <span>Body</span>
              <span className="text-[var(--gc-text-muted)] normal-case tracking-normal font-normal">
                {template.tokens.length} tokens — click to insert
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {template.tokens.map((t) => (
                <button
                  key={t.token}
                  type="button"
                  onClick={() => insertToken(t.token)}
                  title={t.desc}
                  className="px-2 py-1 text-xs font-mono-tech border border-[var(--gc-border)] hover:border-[var(--gc-orange)] hover:text-[var(--gc-orange)] transition-colors"
                  data-testid={`email-tpl-token-${template.key}-${t.token.replace(/[{}]/g, "")}`}
                >{t.token}</button>
              ))}
            </div>
            <RichTextEditor
              value={body || template.defaults.body_html}
              onChange={setBody}
              placeholder="Write the email body…"
            />
          </div>

          {/* CTA button text */}
          <div>
            <div className="tech-label mb-2">Button label (CTA)</div>
            <input
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
              placeholder={template.defaults.cta_text}
              className="gc-input max-w-sm"
              data-testid={`email-tpl-cta-${template.key}`}
            />
            <div className="tech-label mt-1 text-[var(--gc-text-muted)]">
              The link the button opens is generated automatically (job page for job emails, password-set page for the welcome email).
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button onClick={save} disabled={saving || !dirty} className="btn-primary px-5 py-2.5 disabled:opacity-50" data-testid={`email-tpl-save-${template.key}`}>
              {saving ? "Saving…" : dirty ? "Save Template" : "Saved"}
            </button>
            <button onClick={doPreview} disabled={previewing} className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5" data-testid={`email-tpl-preview-${template.key}`}>
              <Eye size={16} weight="bold" /> {previewing ? "Rendering…" : "Preview"}
            </button>
            <button onClick={sendTest} disabled={testing} className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5" data-testid={`email-tpl-test-${template.key}`}>
              <TestTube size={16} weight="bold" /> {testing ? "Sending…" : "Send Test to Me"}
            </button>
            {hasOverride && (
              <button onClick={resetToDefault} className="text-sm text-[var(--gc-text-muted)] link-underline inline-flex items-center gap-1.5 ml-auto" data-testid={`email-tpl-reset-${template.key}`}>
                <ArrowCounterClockwise size={14} weight="bold" /> Reset to default
              </button>
            )}
          </div>
        </div>
      )}

      {previewOpen && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`email-tpl-preview-modal-${template.key}`}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setPreviewOpen(false)} />
          <div className="relative bg-white max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--gc-border)]">
              <div className="min-w-0">
                <div className="eyebrow mb-1">Preview — {template.label}</div>
                <div className="font-display text-xl truncate">{preview.subject}</div>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="text-sm link-underline shrink-0 ml-4" data-testid={`email-tpl-preview-close-${template.key}`}>Close</button>
            </div>
            <iframe
              title="preview"
              className="w-full flex-1"
              srcDoc={preview.wrapped_html}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </div>
  );
}
